import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Box, Typography, Divider } from '@mui/material';
import { ConfigurationAPI } from '../../apis';
import { CONFIGURATION_TYPE_LABELS, VALUE_TYPE_LABELS } from '../../constants/configuration';
import { constants } from '../../constants';
const AddConfigurationModal = ({ open, onClose, onSaved, configuration, itemIndex, existingConfigurations = [] }) => {
    const [selectedType, setSelectedType] = useState('');
    const [key, setKey] = useState('');
    const [value, setValue] = useState('');
    const [valueType, setValueType] = useState('string');
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const isEditMode = !!configuration && itemIndex !== null && itemIndex !== undefined;
    // Effect to populate form when in edit mode
    useEffect(() => {
        if (configuration &&
            open &&
            itemIndex !== null &&
            itemIndex !== undefined) {
            setSelectedType(configuration.type);
            // Use the specific item at itemIndex for editing
            const editItem = configuration.items[itemIndex];
            if (editItem) {
                setKey(editItem.key);
                // Convert value to string for display
                const displayValue = typeof editItem.value === 'object'
                    ? JSON.stringify(editItem.value, null, 2)
                    : String(editItem.value);
                setValue(displayValue);
                // Determine value type
                if (typeof editItem.value === 'boolean') {
                    setValueType('boolean');
                }
                else if (typeof editItem.value === 'number') {
                    setValueType('number');
                }
                else if (typeof editItem.value === 'object') {
                    setValueType('object');
                }
                else {
                    setValueType('string');
                }
            }
        }
    }, [configuration, open, itemIndex]);
    const handleClose = () => {
        // Reset form
        setSelectedType('');
        setKey('');
        setValue('');
        setValueType('string');
        setErrors({});
        onClose();
    };
    const validateValueType = (type, val) => {
        try {
            if (type === 'number') {
                const num = Number(val);
                if (Number.isNaN(num)) {
                    return constants.configuration_modal_error_number_invalid;
                }
            }
            else if (type === 'boolean') {
                const lower = val.toLowerCase();
                if (lower !== 'true' && lower !== 'false') {
                    return constants.configuration_modal_error_boolean_invalid;
                }
            }
            else if (type === 'object') {
                JSON.parse(val); // This will throw if invalid JSON
            }
            return null;
        }
        catch {
            return constants.configuration_modal_error_json_invalid;
        }
    };
    const validateForm = () => {
        const newErrors = {};
        if (!selectedType) {
            newErrors.type = constants.configuration_modal_error_select_type;
        }
        if (!key.trim()) {
            newErrors.key = constants.configuration_modal_error_key_required;
        }
        if (!value.trim()) {
            newErrors.value = constants.configuration_modal_error_value_required;
        }
        // Validate value based on type
        if (value.trim()) {
            const validationError = validateValueType(valueType, value);
            if (validationError) {
                newErrors.value = validationError;
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const convertValue = (val, type) => {
        switch (type) {
            case 'number':
                return Number(val);
            case 'boolean':
                return val.toLowerCase() === 'true';
            case 'object':
                return JSON.parse(val);
            default:
                return val;
        }
    };
    const getButtonText = () => {
        if (loading) {
            return isEditMode
                ? constants.configuration_modal_button_updating
                : constants.configuration_modal_button_adding;
        }
        return isEditMode
            ? constants.configuration_modal_button_update
            : constants.configuration_modal_button_add;
    };
    const handleSubmit = async () => {
        if (!validateForm())
            return;
        try {
            setLoading(true);
            if (isEditMode &&
                configuration &&
                itemIndex !== null &&
                itemIndex !== undefined) {
                // Update existing configuration - update the specific item while keeping others
                const updatedItems = [...configuration.items];
                updatedItems[itemIndex] = {
                    key: key.trim(),
                    value: convertValue(value.trim(), valueType)
                };
                const updateData = {
                    resourceType: 'Configuration',
                    type: selectedType,
                    items: updatedItems,
                    details: configuration.details || {}
                };
                await ConfigurationAPI.updateConfiguration(configuration.id, updateData);
            }
            else {
                // Adding new item - check if configuration of this type already exists
                const existingConfig = existingConfigurations.find((config) => config.type === selectedType);
                if (existingConfig) {
                    // Append to existing configuration's items array
                    const updatedItems = [
                        ...existingConfig.items,
                        {
                            key: key.trim(),
                            value: convertValue(value.trim(), valueType)
                        }
                    ];
                    const updateData = {
                        resourceType: 'Configuration',
                        type: selectedType,
                        items: updatedItems,
                        details: existingConfig.details || {}
                    };
                    await ConfigurationAPI.updateConfiguration(existingConfig.id, updateData);
                }
                else {
                    // Create new configuration
                    const configurationData = {
                        resourceType: 'Configuration',
                        type: selectedType,
                        items: [
                            {
                                key: key.trim(),
                                value: convertValue(value.trim(), valueType)
                            }
                        ],
                        details: {}
                    };
                    await ConfigurationAPI.createConfiguration(configurationData);
                }
            }
            onSaved();
            handleClose();
        }
        catch {
            setErrors({
                submit: isEditMode
                    ? constants.configuration_modal_submit_update_failed
                    : constants.configuration_modal_submit_create_failed
            });
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsxs(Dialog, { open: open, onClose: handleClose, maxWidth: "sm", fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                borderRadius: 2,
                minHeight: 400
            }
        }, children: [_jsx(DialogTitle, { sx: {
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#212121',
                    pb: 1
                }, children: isEditMode
                    ? constants.configuration_modal_title_edit
                    : constants.configuration_modal_title_add }), _jsx(Divider, {}), _jsx(DialogContent, { sx: { py: 3 }, children: _jsxs(Box, { sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        width: '100%'
                    }, children: [_jsx(Box, { children: _jsx(TextField, { select: true, fullWidth: true, label: _jsxs(Box, { component: "span", children: [constants.configuration_modal_field_system, ' ', _jsx(Box, { component: "span", sx: { color: '#da291c' }, children: "*" })] }), value: selectedType, onChange: (e) => setSelectedType(e.target.value), error: !!errors.type, helperText: errors.type, size: "medium", sx: {
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1
                                    }
                                }, children: Object.entries(CONFIGURATION_TYPE_LABELS).map(([optionKey, label]) => (_jsx(MenuItem, { value: optionKey, children: label }, optionKey))) }) }), _jsx(Box, { children: _jsx(TextField, { fullWidth: true, label: _jsxs(Box, { component: "span", children: [constants.configuration_modal_field_key, ' ', _jsx(Box, { component: "span", sx: { color: '#da291c' }, children: "*" })] }), placeholder: constants.configuration_modal_field_key, value: key, onChange: (e) => setKey(e.target.value), error: !!errors.key, helperText: errors.key, size: "medium", sx: {
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1
                                    }
                                } }) }), _jsx(Box, { children: _jsx(TextField, { select: true, fullWidth: true, label: _jsxs(Box, { component: "span", children: [constants.configuration_modal_field_value_type, ' ', _jsx(Box, { component: "span", sx: { color: '#da291c' }, children: "*" })] }), value: valueType, onChange: (e) => setValueType(e.target.value), error: !!errors.valueType, helperText: errors.valueType, size: "medium", sx: {
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1
                                    }
                                }, children: Object.entries(VALUE_TYPE_LABELS).map(([typeKey, label]) => (_jsx(MenuItem, { value: typeKey, children: label }, typeKey))) }) }), _jsx(Box, { children: valueType === 'object' ? (_jsx(TextField, { fullWidth: true, multiline: true, minRows: 10, maxRows: 24, label: _jsxs(Box, { component: "span", children: [constants.configuration_modal_field_value, ' ', _jsx(Box, { component: "span", sx: { color: '#da291c' }, children: "*" })] }), placeholder: constants.configuration_modal_field_value, value: value, onChange: (e) => setValue(e.target.value), error: !!errors.value, helperText: errors.value, variant: "outlined", sx: {
                                    '& .MuiOutlinedInput-root': {
                                        fontFamily: 'monospace',
                                        fontSize: '14px',
                                        lineHeight: 1.62,
                                        borderRadius: 1,
                                        alignItems: 'flex-start',
                                        // Let MUI handle background and padding
                                        '& fieldset': {
                                            border: '2px solid #26890D'
                                        }
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                        borderColor: '#1e6b0a'
                                    },
                                    '& .MuiOutlinedInput-root.Mui-error fieldset': {
                                        borderColor: '#da291c'
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'monospace',
                                        fontSize: '14px',
                                        lineHeight: 1.62,
                                        padding: '16px',
                                        minHeight: '180px',
                                        resize: 'vertical',
                                        overflow: 'auto'
                                    }
                                } })) : (_jsx(TextField, { fullWidth: true, label: _jsxs(Box, { component: "span", children: [constants.configuration_modal_field_value, ' ', _jsx(Box, { component: "span", sx: { color: '#da291c' }, children: "*" })] }), placeholder: constants.configuration_modal_field_value, value: value, onChange: (e) => setValue(e.target.value), error: !!errors.value, helperText: errors.value, size: "medium", sx: {
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1
                                    }
                                } })) }), errors.submit && (_jsx(Typography, { color: "error", variant: "body2", children: errors.submit }))] }) }), _jsxs(DialogActions, { sx: {
                    p: 3,
                    pt: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2
                }, children: [_jsx(Button, { onClick: handleClose, sx: {
                            color: '#26890D',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: 'rgba(38, 137, 13, 0.04)'
                            }
                        }, children: constants.cancel }), _jsx(Button, { onClick: handleSubmit, disabled: loading || !selectedType || !key.trim() || !value.trim(), variant: "contained", sx: {
                            backgroundColor: '#26890D',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#1e6b0a'
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(38, 137, 13, 0.4)',
                                color: '#fff'
                            }
                        }, children: getButtonText() })] })] }));
};
export default AddConfigurationModal;
